import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { NoteType, SkuAnnotation, SkuAnnotationArrowhead, SkuAnnotationLineStyle, SkuAnnotationType, StyleRun, Vertex2 } from '@rta/rta-blueprint-library';
import { AppContext } from '@r-app/app-context';

import {
	applyAnnotationBoxPatch,
	applyStyleRun,
	getAnnotationFillPaint,
	getAnnotationStrokePaint,
	isAnnotationFilled,
	isAnnotationStroked,
	normalizeAnnotationFontSize,
	remapStyleRuns,
	renderAnnotationTextRuns,
	resolveAnnotationStyleRuns,
	type RenderAnnotationTextRunsOptions,
	type StyleRunPatch,
	toStyledTextSegments,
	toStyleRunCss
} from './helpers';
import { useDraggableSvg } from '@r-services/ui-interactions';
import { useAnnotationStylePreview, useSkuAnnotation } from './sku-annotation-context';
import type { SkuAnnotationTool } from './sku-annotation-toolbar';

export type RenderSkuAnnotationProps = {
	annotation: SkuAnnotation;
	activeTool: SkuAnnotationTool;
	isSelected: boolean;
	isEditing: boolean;
	fontSizeFactor: number;
	onSelectAnnotation: (id: string | null) => void;
	onEditAnnotation?: (id: string) => void;
	onCloseEdit: () => void;
	onContextMenu?: (e: React.MouseEvent, annotationId: string) => void;
};

const RESIZE_HANDLE_SIZE = 1.5;
const ENDPOINT_HANDLE_RADIUS = 1;
const MIN_TEXT_BOX_WIDTH = 10;
const MIN_TEXT_BOX_HEIGHT = 5;
const MIN_SHAPE_SIZE = 3;

/**
 * Converts a SkuAnnotationLineStyle to an SVG stroke-dasharray value.
 * Uses fixed SVG-unit values so dashes are visible at any stroke width.
 */
const getStrokeDasharray = (lineStyle: SkuAnnotationLineStyle): string | undefined => {
	if (lineStyle === SkuAnnotationLineStyle.Dashed) return '1.5,1';
	if (lineStyle === SkuAnnotationLineStyle.Dotted) return '0.3,0.7';
	return undefined;
};

const justifyForVerticalAlign = (verticalAlign: string): React.CSSProperties['justifyContent'] =>
	verticalAlign === 'middle' ? 'center' : verticalAlign === 'bottom' ? 'flex-end' : 'flex-start';

/** A text box's border is a fixed hairline on this surface, so only whether it is stroked at all is read */
const TEXT_BOX_STROKE_WIDTH = 0.2;

/** Selection outline weight for an annotation whose own stroke is off, so a no-border object still shows as selected */
const UNSTROKED_SELECTION_WIDTH = 0.2;

/** Hit band for a doodle whose stroke is off, mirroring the floors its line/shape siblings already have */
const MIN_DOODLE_HIT_WIDTH = 3;

/**
 * Defines the 8 resize handle positions (4 corners + 4 edge midpoints).
 * Each handle specifies which edges it affects and its cursor style.
 */
type ResizeHandlePosition = 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

type ResizeHandleConfig = {
	position: ResizeHandlePosition;
	cursor: string;
	affectsLeft: boolean;
	affectsTop: boolean;
	affectsRight: boolean;
	affectsBottom: boolean;
};

const RESIZE_HANDLES: Array<ResizeHandleConfig> = [
	{ position: 'top-left', cursor: 'nwse-resize', affectsLeft: true, affectsTop: true, affectsRight: false, affectsBottom: false },
	{ position: 'top-center', cursor: 'ns-resize', affectsLeft: false, affectsTop: true, affectsRight: false, affectsBottom: false },
	{ position: 'top-right', cursor: 'nesw-resize', affectsLeft: false, affectsTop: true, affectsRight: true, affectsBottom: false },
	{ position: 'middle-left', cursor: 'ew-resize', affectsLeft: true, affectsTop: false, affectsRight: false, affectsBottom: false },
	{ position: 'middle-right', cursor: 'ew-resize', affectsLeft: false, affectsTop: false, affectsRight: true, affectsBottom: false },
	{ position: 'bottom-left', cursor: 'nesw-resize', affectsLeft: true, affectsTop: false, affectsRight: false, affectsBottom: true },
	{ position: 'bottom-center', cursor: 'ns-resize', affectsLeft: false, affectsTop: false, affectsRight: false, affectsBottom: true },
	{ position: 'bottom-right', cursor: 'nwse-resize', affectsLeft: false, affectsTop: false, affectsRight: true, affectsBottom: true }
];

/**
 * Calculates the SVG-space x/y position for a given resize handle.
 */
const getHandlePosition = (handle: ResizeHandleConfig, width: number, height: number): { x: number; y: number } => {
	const halfHandle = RESIZE_HANDLE_SIZE / 2;

	switch (handle.position) {
		case 'top-left': return { x: -halfHandle, y: -halfHandle };
		case 'top-center': return { x: (width / 2) - halfHandle, y: -halfHandle };
		case 'top-right': return { x: width - halfHandle, y: -halfHandle };
		case 'middle-left': return { x: -halfHandle, y: (height / 2) - halfHandle };
		case 'middle-right': return { x: width - halfHandle, y: (height / 2) - halfHandle };
		case 'bottom-left': return { x: -halfHandle, y: height - halfHandle };
		case 'bottom-center': return { x: (width / 2) - halfHandle, y: height - halfHandle };
		case 'bottom-right': return { x: width - halfHandle, y: height - halfHandle };
	}
};

type ResizeStartState = {
	clientX: number;
	clientY: number;
	annotationX: number;
	annotationY: number;
	width: number;
	height: number;
	handle: ResizeHandleConfig;
};

type LineDragState = {
	clientX: number;
	clientY: number;
	startX: number;
	startY: number;
	endX: number;
	endY: number;
	endpoint: 'start' | 'end' | 'body';
};

export const RenderSkuAnnotation = (props: RenderSkuAnnotationProps): React.ReactElement => {
	const { annotation, activeTool, isSelected, isEditing, fontSizeFactor, onSelectAnnotation, onEditAnnotation, onCloseEdit, onContextMenu } = props;
	const { controller } = useContext(AppContext) ?? {};
	const kitchen = controller?.kitchen;
	const { pendingSaveRef, setActiveTextSelection, setFormatSelection, applyStyleRef, liveStyleRunsRef, toolbarInteractingRef } = useSkuAnnotation();

	const groupRef = useRef<SVGGElement | null>(null);
	const textMeasureRef = useRef<HTMLDivElement | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);

	const [editText, setEditText] = useState('');
	const [editRuns, setEditRuns] = useState<Array<StyleRun>>([]);
	const editTextRef = useRef('');
	const editRunsRef = useRef<Array<StyleRun>>([]);
	const formatRangeRef = useRef<{ start: number; end: number } | null>(null);
	const [pendingSelect, setPendingSelect] = useState<string | null>(null);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeStart, setResizeStart] = useState<ResizeStartState | null>(null);
	const [isRotating, setIsRotating] = useState(false);
	const rotateStartRef = useRef<{ startAngle: number; startRotation: number } | null>(null);
	const rotatePreviewRef = useRef<number | null>(null);
	const dragPositionRef = useRef<{ x: number; y: number } | null>(null);
	const [lineDrag, setLineDrag] = useState<LineDragState | null>(null);

	// Track previous font size to detect changes for auto-resize
	const prevFontSizeRef = useRef<number>(annotation.fontSize * fontSizeFactor);

	const isLine = annotation.type === SkuAnnotationType.Line;

	// The colour a picker is still dragging: painted for feedback only, so the model is written once on commit (#390)
	const stylePreview = useAnnotationStylePreview(annotation.id);
	const previewPatch = stylePreview && stylePreview.range === null ? stylePreview.patch : null;
	// Each channel previews under its own key, so dragging the border picker cannot recolour the text
	const previewTextColor = previewPatch?.color ?? null;
	const previewFillColor = previewPatch?.fillColor ?? null;
	const previewStrokeColor = previewPatch?.strokeColor ?? null;

	// The runs a read-mode text box draws: legacy colorRuns as the base layer with styleRuns merged on top
	const styleRuns = useMemo((): Array<StyleRun> => resolveAnnotationStyleRuns(annotation), [annotation, annotation.colorRuns, annotation.styleRuns]);

	// Seed the local edit session; runs stay local until save so a keystroke or a colour drag never writes the model
	useEffect(() => {
		if (!isEditing) return;
		const initialRuns = resolveAnnotationStyleRuns(annotation);
		editTextRef.current = annotation.text;
		editRunsRef.current = initialRuns;
		formatRangeRef.current = null;
		setEditText(annotation.text);
		setEditRuns(initialRuns);
		setActiveTextSelection(null);
		liveStyleRunsRef.current = { annotationId: annotation.id, runs: initialRuns };
		const timer = setTimeout(() => textareaRef.current?.focus(), 30);
		return () => {
			clearTimeout(timer);
			if (liveStyleRunsRef.current?.annotationId === annotation.id) liveStyleRunsRef.current = null;
		};
	}, [isEditing, annotation, liveStyleRunsRef, setActiveTextSelection]);

	// The toolbar's lit states read these before they are committed, so every local run write goes through one place
	const writeEditRuns = useCallback((runs: Array<StyleRun>): void => {
		editRunsRef.current = runs;
		liveStyleRunsRef.current = { annotationId: annotation.id, runs };
		setEditRuns(runs);
	}, [annotation.id, liveStyleRunsRef]);

	const endEditSession = useCallback((): void => {
		formatRangeRef.current = null;
		setActiveTextSelection(null);
		setFormatSelection(null);
		onCloseEdit();
	}, [onCloseEdit, setActiveTextSelection, setFormatSelection]);

	const handleEditSave = useCallback((): void => {
		const nextText = editTextRef.current;
		const nextRuns = editRunsRef.current;
		if (nextRuns.length > 0 || annotation.styleRuns.length > 0) annotation.styleRuns = nextRuns;
		if (annotation.colorRuns.length > 0) annotation.colorRuns = [];
		annotation.text = nextText;
		if (kitchen) {
			const existingNotes = kitchen.notes.getArtifactNotes(annotation.id, NoteType.Annotation);
			existingNotes.forEach((note) => note.delete());
			if (nextText.trim()) {
				kitchen.notes.add({ message: `[Annotation] ${nextText}`, artifactId: annotation.id, type: NoteType.Annotation });
			}
		}
		endEditSession();
	}, [annotation, endEditSession, kitchen]);

	const handleEditChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
		const { value } = e.target;
		const nextRuns = remapStyleRuns({ runs: editRunsRef.current, previousText: editTextRef.current, nextText: value });
		editTextRef.current = value;
		setEditText(value);
		writeEditRuns(nextRuns);
		// Typing collapses the highlight, so the durable range goes with it -- keeping it would format characters the user can no longer see selected
		if (!formatRangeRef.current) return;
		formatRangeRef.current = null;
		setFormatSelection(null);
	}, [setFormatSelection, writeEditRuns]);

	// #390: keep the last NON-EMPTY range so reaching for the toolbar cannot destroy what a format applies to
	const handleTextSelect = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>): void => {
		const { selectionStart, selectionEnd, value } = e.currentTarget;
		if (selectionStart === selectionEnd) { setActiveTextSelection(null); return; }
		formatRangeRef.current = { start: selectionStart, end: selectionEnd };
		setActiveTextSelection({ start: selectionStart, end: selectionEnd, text: value });
		setFormatSelection({ annotationId: annotation.id, start: selectionStart, end: selectionEnd });
	}, [annotation.id, setActiveTextSelection, setFormatSelection]);

	const applyStyleToWholeBox = useCallback((patch: StyleRunPatch): void => {
		writeEditRuns(applyAnnotationBoxPatch({ annotation, patch, runs: editRunsRef.current, textLength: editTextRef.current.length }));
	}, [annotation, writeEditRuns]);

	// Registered in context while this editor is live so the toolbar applies formats through the surface that owns the selection
	const applyStyleToSelection = useCallback((patch: StyleRunPatch): void => {
		const textarea = textareaRef.current;
		const liveRange = textarea && textarea.selectionStart !== textarea.selectionEnd
			? { start: textarea.selectionStart, end: textarea.selectionEnd }
			: null;
		const range = liveRange ?? formatRangeRef.current;
		if (!range) { applyStyleToWholeBox(patch); return; }

		const { start, end } = range;
		formatRangeRef.current = range;
		writeEditRuns(applyStyleRun({ runs: editRunsRef.current, start, end, patch }));
		setFormatSelection({ annotationId: annotation.id, start, end });
		// Never yank focus out of a control the user is still holding (a native colour picker closes if we do)
		requestAnimationFrame(() => {
			if (document.activeElement?.closest('[data-sku-toolbar]')) return;
			textareaRef.current?.focus();
			textareaRef.current?.setSelectionRange(start, end);
		});
	}, [annotation.id, applyStyleToWholeBox, setFormatSelection, writeEditRuns]);

	useEffect(() => {
		if (!isEditing) return;
		applyStyleRef.current = applyStyleToSelection;
		return () => { applyStyleRef.current = null; };
	}, [isEditing, applyStyleToSelection, applyStyleRef]);

	// Register the save callback so click-outside can save before unmounting the textarea
	useEffect(() => {
		if (isEditing) {
			pendingSaveRef.current = handleEditSave;
			return () => { pendingSaveRef.current = null; };
		}
	}, [isEditing, handleEditSave, pendingSaveRef]);

	// A blur into the toolbar must not end the edit session -- that is what destroyed the selection before a colour landed
	const handleEditBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>): void => {
		if (toolbarInteractingRef.current) return;
		if ((e.relatedTarget as HTMLElement | null)?.closest('[data-sku-toolbar]')) return;
		handleEditSave();
	}, [handleEditSave, toolbarInteractingRef]);

	const handleEditKeyDown = useCallback((e: React.KeyboardEvent): void => {
		if (e.key === 'Escape') { endEditSession(); return; }
		if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSave(); }
	}, [endEditSession, handleEditSave]);

	// Defer selection to avoid setState during render (useDraggableSvg fires onDragStart from useMemo)
	useEffect(() => {
		if (pendingSelect !== null) {
			onSelectAnnotation(pendingSelect);
			setPendingSelect(null);
		}
	}, [pendingSelect, onSelectAnnotation]);

	// Normalize old-format fontSize (stored 8–72 using 0.08 factor) to SVG units
	const resolvedFontSize = normalizeAnnotationFontSize(annotation.fontSize);

	// A run's fontSize is in the same unit as the box's own, so it normalizes and scales identically
	const textRunOptions = useMemo((): RenderAnnotationTextRunsOptions => ({
		resolveFontSize: (value: number): number => normalizeAnnotationFontSize(value) * fontSizeFactor
	}), [fontSizeFactor]);

	// One metric set shared by the read-mode div, the edit textarea and its styled mirror, so they cannot drift
	const textBoxTextStyle = useMemo((): React.CSSProperties => ({
		width: '100%',
		height: '100%',
		padding: '1px',
		boxSizing: 'border-box',
		fontSize: `${resolvedFontSize * fontSizeFactor}px`,
		fontWeight: annotation.fontWeight ?? '500',
		fontStyle: annotation.fontStyle || 'normal',
		fontFamily: `${annotation.fontFamily || 'Arial'}, sans-serif`,
		lineHeight: '1.2',
		overflowWrap: 'break-word',
		wordWrap: 'break-word',
		wordBreak: 'break-word',
		whiteSpace: 'pre-wrap',
		textAlign: (annotation.textAlign || 'left') as React.CSSProperties['textAlign']
	}), [resolvedFontSize, fontSizeFactor, annotation.fontWeight, annotation.fontStyle, annotation.fontFamily, annotation.textAlign]);

	const getGroupTransform = useCallback((x: number, y: number, rotation: number): string => {
		const { x2, y2, width, height } = annotation;
		if (isLine) return rotation ? `rotate(${rotation}, ${(x + x2) / 2}, ${(y + y2) / 2})` : '';
		return `translate(${x}, ${y})${rotation ? ` rotate(${rotation}, ${width / 2}, ${height / 2})` : ''}`;
	}, [annotation, isLine]);

	useDraggableSvg({
		ref: groupRef,
		enabled: [!isLine && activeTool === 'select' && !isResizing && !isEditing, !isLine && activeTool === 'select' && !isResizing && !isEditing],
		cursor: ['inherit', 'move'],
		position: { x: annotation.x, y: annotation.y },
		bBox: { origin: new Vertex2(), size: { width: 1000, height: 1000, depth: 0 }, padding: [0, 0, 0, 0] },
		onDragStart: () => {
			if (!isResizing) {
				setPendingSelect(annotation.id);
			}
		},
		// Paint the gesture straight onto the group's transform; the model is written once on release
		onDragging: (e) => {
			if (isResizing) return false;
			const { position: { new: newPosition } } = e;
			dragPositionRef.current = newPosition;
			groupRef.current?.setAttribute('transform', getGroupTransform(newPosition.x, newPosition.y, annotation.rotation));
			return true;
		},
		// useDraggableSvg calls onDragEnd from a useMemo, so the publishing write is deferred out of the render pass
		onDragEnd: () => {
			const dragPosition = dragPositionRef.current;
			dragPositionRef.current = null;
			if (dragPosition) {
				setTimeout(() => {
					annotation.x = dragPosition.x;
					annotation.y = dragPosition.y;
				}, 0);
			}
			return true;
		}
	});

	/**
	 * Converts a screen-space pixel delta to SVG-space units using the actual SVG CTM.
	 * Falls back to an approximation if the CTM is unavailable.
	 */
	const screenDeltaToSvg = useCallback((screenDx: number, screenDy: number): { dx: number; dy: number } => {
		const svg = groupRef.current?.ownerSVGElement;
		if (svg) {
			const ctm = svg.getScreenCTM();
			if (ctm) {
				return { dx: screenDx / ctm.a, dy: screenDy / ctm.d };
			}
		}
		const scaleFactor = 0.1 / fontSizeFactor;
		return { dx: screenDx * scaleFactor, dy: screenDy * scaleFactor };
	}, [fontSizeFactor]);

	const handleResizeMouseDown = useCallback((e: React.MouseEvent, handle: ResizeHandleConfig): void => {
		e.stopPropagation();
		e.preventDefault();
		setIsResizing(true);
		setResizeStart({
			clientX: e.clientX,
			clientY: e.clientY,
			annotationX: annotation.x,
			annotationY: annotation.y,
			width: annotation.width,
			height: annotation.height,
			handle
		});
	}, [annotation.x, annotation.y, annotation.width, annotation.height]);

	// Handle resize mouse move and mouse up for directional resizing
	useEffect(() => {
		if (!isResizing || !resizeStart) return;

		const { handle } = resizeStart;
		const minW = MIN_TEXT_BOX_WIDTH;
		const minH = MIN_TEXT_BOX_HEIGHT;

		const handleMouseMove = (e: MouseEvent): void => {
			const { dx: deltaX, dy: deltaY } = screenDeltaToSvg(
				e.clientX - resizeStart.clientX,
				e.clientY - resizeStart.clientY
			);

			let newX = resizeStart.annotationX;
			let newY = resizeStart.annotationY;
			let newWidth = resizeStart.width;
			let newHeight = resizeStart.height;

			if (handle.affectsLeft) {
				const maxLeftDelta = resizeStart.width - minW;
				const clampedDelta = Math.min(deltaX, maxLeftDelta);
				newX = resizeStart.annotationX + clampedDelta;
				newWidth = resizeStart.width - clampedDelta;
			} else if (handle.affectsRight) {
				newWidth = Math.max(minW, resizeStart.width + deltaX);
			}

			if (handle.affectsTop) {
				const maxTopDelta = resizeStart.height - minH;
				const clampedDelta = Math.min(deltaY, maxTopDelta);
				newY = resizeStart.annotationY + clampedDelta;
				newHeight = resizeStart.height - clampedDelta;
			} else if (handle.affectsBottom) {
				newHeight = Math.max(minH, resizeStart.height + deltaY);
			}

			annotation.x = newX;
			annotation.y = newY;
			annotation.width = newWidth;
			annotation.height = newHeight;
		};

		const handleMouseUp = (): void => {
			setIsResizing(false);
			setResizeStart(null);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isResizing, resizeStart, annotation, screenDeltaToSvg, isLine]);

	// Rotation center: TextBox/Rectangle/Ellipse rotate about their box center, Line about its midpoint
	const getRotationCenter = useCallback((): { centerX: number; centerY: number } => {
		if (isLine) return { centerX: (annotation.x + annotation.x2) / 2, centerY: (annotation.y + annotation.y2) / 2 };
		return { centerX: annotation.x + annotation.width / 2, centerY: annotation.y + annotation.height / 2 };
	}, [annotation, isLine]);

	// Rotation handle: mouse down starts rotation tracking
	const handleRotateMouseDown = useCallback((e: React.MouseEvent): void => {
		e.stopPropagation();
		e.preventDefault();
		setIsRotating(true);

		// Compute the center of the box in screen space
		const svg = groupRef.current?.ownerSVGElement;
		const ctm = svg?.getScreenCTM();
		const { centerX, centerY } = getRotationCenter();
		let screenCenterX = centerX;
		let screenCenterY = centerY;
		if (ctm) {
			const pt = svg.createSVGPoint();
			pt.x = centerX;
			pt.y = centerY;
			const screenPt = pt.matrixTransform(ctm);
			screenCenterX = screenPt.x;
			screenCenterY = screenPt.y;
		}

		const startAngle = Math.atan2(e.clientY - screenCenterY, e.clientX - screenCenterX) * 180 / Math.PI;
		rotateStartRef.current = { startAngle, startRotation: annotation.rotation ?? 0 };
	}, [annotation, getRotationCenter]);

	// Rotation handle: mouse move and up
	useEffect(() => {
		if (!isRotating || !rotateStartRef.current) return;

		// The pivot cannot move mid-gesture, so its screen position is resolved once instead of per frame
		const svg = groupRef.current?.ownerSVGElement;
		const ctm = svg?.getScreenCTM();
		const { centerX, centerY } = getRotationCenter();
		let screenCenterX = centerX;
		let screenCenterY = centerY;
		if (svg && ctm) {
			const pt = svg.createSVGPoint();
			pt.x = centerX;
			pt.y = centerY;
			const screenPt = pt.matrixTransform(ctm);
			screenCenterX = screenPt.x;
			screenCenterY = screenPt.y;
		}

		const handleMouseMove = (e: MouseEvent): void => {
			const currentAngle = Math.atan2(e.clientY - screenCenterY, e.clientX - screenCenterX) * 180 / Math.PI;
			const { startAngle, startRotation } = rotateStartRef.current;
			const delta = currentAngle - startAngle;
			const rotation = ((startRotation + delta) % 360 + 360) % 360;
			rotatePreviewRef.current = rotation;
			groupRef.current?.setAttribute('transform', getGroupTransform(annotation.x, annotation.y, rotation));
		};

		const handleMouseUp = (): void => {
			const rotation = rotatePreviewRef.current;
			rotatePreviewRef.current = null;
			if (rotation !== null) annotation.rotation = rotation;
			setIsRotating(false);
			rotateStartRef.current = null;
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isRotating, annotation, getGroupTransform, getRotationCenter]);

	// Line drag: handles body drag and endpoint drag
	const handleLineDragStart = useCallback((e: React.MouseEvent, endpoint: 'start' | 'end' | 'body'): void => {
		e.stopPropagation();
		e.preventDefault();
		onSelectAnnotation(annotation.id);
		setLineDrag({
			clientX: e.clientX,
			clientY: e.clientY,
			startX: annotation.x,
			startY: annotation.y,
			endX: annotation.x2,
			endY: annotation.y2,
			endpoint
		});
	}, [annotation.x, annotation.y, annotation.x2, annotation.y2, annotation.id, onSelectAnnotation]);

	useEffect(() => {
		if (!lineDrag) return;

		const handleMouseMove = (e: MouseEvent): void => {
			const { dx, dy } = screenDeltaToSvg(
				e.clientX - lineDrag.clientX,
				e.clientY - lineDrag.clientY
			);

			if (lineDrag.endpoint === 'body') {
				annotation.x = lineDrag.startX + dx;
				annotation.y = lineDrag.startY + dy;
				annotation.x2 = lineDrag.endX + dx;
				annotation.y2 = lineDrag.endY + dy;
			} else if (lineDrag.endpoint === 'start') {
				annotation.x = lineDrag.startX + dx;
				annotation.y = lineDrag.startY + dy;
			} else {
				annotation.x2 = lineDrag.endX + dx;
				annotation.y2 = lineDrag.endY + dy;
			}
		};

		const handleMouseUp = (): void => {
			setLineDrag(null);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [lineDrag, annotation, screenDeltaToSvg]);

	/**
	 * Auto-resize: when the effective font size changes, measure the text content
	 * and grow the text box if the content no longer fits.
	 */
	useEffect(() => {
		if (annotation.type !== SkuAnnotationType.TextBox) return;
		if (!annotation.text) return;

		// A run can carry a bigger fontSize than the box, so growth is judged on the largest size actually rendered
		const largestFontSize = styleRuns.reduce((largest, { fontSize }) => Math.max(largest, fontSize ?? 0), annotation.fontSize);
		const effectiveFontSize = normalizeAnnotationFontSize(largestFontSize) * fontSizeFactor;
		const previousFontSize = prevFontSizeRef.current;
		prevFontSizeRef.current = effectiveFontSize;

		if (effectiveFontSize <= previousFontSize) return;

		const measureDiv = document.createElement('div');
		measureDiv.style.position = 'absolute';
		measureDiv.style.visibility = 'hidden';
		measureDiv.style.pointerEvents = 'none';
		const svg = groupRef.current?.ownerSVGElement;
		const ctmScale = svg?.getScreenCTM()?.a ?? (fontSizeFactor * 10);
		const widthInPx = (annotation.width - 2) * ctmScale;
		measureDiv.style.width = `${widthInPx}px`;
		measureDiv.style.fontSize = `${normalizeAnnotationFontSize(annotation.fontSize) * fontSizeFactor * ctmScale}px`;
		measureDiv.style.fontWeight = annotation.fontWeight || '500';
		measureDiv.style.fontFamily = `${annotation.fontFamily || 'Arial'}, sans-serif`;
		measureDiv.style.fontStyle = annotation.fontStyle || 'normal';
		measureDiv.style.lineHeight = '1.2';
		measureDiv.style.padding = '1px';
		measureDiv.style.boxSizing = 'border-box';
		measureDiv.style.overflowWrap = 'break-word';
		measureDiv.style.wordWrap = 'break-word';
		measureDiv.style.wordBreak = 'break-word';
		measureDiv.style.whiteSpace = 'pre-wrap';

		// Measure the same span markup the renderer emits, so a bold or larger run cannot under-size the box
		toStyledTextSegments(annotation.text, styleRuns).forEach(({ text, style }) => {
			if (!style) { measureDiv.appendChild(document.createTextNode(text)); return; }
			const span = document.createElement('span');
			Object.assign(span.style, toStyleRunCss(style, { resolveFontSize: (value: number): number => normalizeAnnotationFontSize(value) * fontSizeFactor * ctmScale }));
			span.textContent = text;
			measureDiv.appendChild(span);
		});
		document.body.appendChild(measureDiv);

		const measuredHeight = measureDiv.scrollHeight;
		const measuredWidth = measureDiv.scrollWidth;
		document.body.removeChild(measureDiv);

		const requiredHeight = (measuredHeight / ctmScale) + 2;
		const requiredWidth = (measuredWidth / ctmScale) + 2;

		if (requiredHeight > annotation.height) {
			annotation.height = Math.max(MIN_TEXT_BOX_HEIGHT, requiredHeight);
		}
		if (requiredWidth > annotation.width) {
			annotation.width = Math.max(MIN_TEXT_BOX_WIDTH, requiredWidth);
		}
	}, [annotation.fontSize, fontSizeFactor, annotation.text, annotation.type, annotation, styleRuns]);

	// === Shared resize handles renderer ===
	const renderResizeHandles = (width: number, height: number): React.ReactElement | null => {
		if (!isSelected || activeTool !== 'select') return null;
		return (
			<>
				{RESIZE_HANDLES.map((handle) => {
					const { x: hx, y: hy } = getHandlePosition(handle, width, height);
					return (
						<rect
							key={handle.position}
							x={hx}
							y={hy}
							width={RESIZE_HANDLE_SIZE}
							height={RESIZE_HANDLE_SIZE}
							fill="white"
							stroke="#3182ce"
							strokeWidth={0.15}
							style={{ cursor: handle.cursor, pointerEvents: 'auto' }}
							onMouseDown={(e) => handleResizeMouseDown(e, handle)}
						/>
					);
				})}
			</>
		);
	};

	// === Shared rotation handle renderer: circle above top-center with connecting line ===
	const ROTATE_HANDLE_DISTANCE = 4;
	const ROTATE_HANDLE_RADIUS = 1;
	const renderRotationHandle = (cx: number, cy: number = -1): React.ReactElement | null => {
		if (!isSelected || isEditing || activeTool !== 'select') return null;
		const handleY = cy - ROTATE_HANDLE_DISTANCE;
		return (
			<>
				<line x1={cx} y1={cy} x2={cx} y2={handleY} stroke="#3182ce" strokeWidth={0.15} />
				<circle
					cx={cx} cy={handleY}
					r={ROTATE_HANDLE_RADIUS}
					fill="white" stroke="#3182ce" strokeWidth={0.15}
					style={{ cursor: 'grab', pointerEvents: 'auto' }}
					onMouseDown={handleRotateMouseDown}
				/>
			</>
		);
	};

	// ===========================
	// TextBox
	// ===========================
	if (annotation.type === SkuAnnotationType.TextBox) {
		const { fill, fillOpacity } = getAnnotationFillPaint({ annotation, previewColor: previewFillColor });
		const { stroke, strokeWidth, strokeDasharray } = getAnnotationStrokePaint({
			annotation,
			width: TEXT_BOX_STROKE_WIDTH,
			dasharray: getStrokeDasharray(annotation.lineStyle),
			previewColor: previewStrokeColor
		});

		return (
			<g key={annotation.id} id={annotation.id} ref={groupRef}
				transform={getGroupTransform(annotation.x, annotation.y, annotation.rotation)}
				style={{ cursor: isEditing ? 'default' : activeTool === 'select' && !isResizing && !isRotating ? 'move' : 'default', pointerEvents: 'auto' }}
				onClick={(e) => { e.stopPropagation(); if (activeTool === 'select' && !isEditing) onSelectAnnotation(annotation.id); }}
				onDoubleClick={(e) => { e.stopPropagation(); if (activeTool === 'select' && onEditAnnotation) onEditAnnotation(annotation.id); }}
				onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (onContextMenu) onContextMenu(e, annotation.id); }}
			>
				{/* Selection / editing border */}
				{(isSelected || isEditing) && (
					<rect x={-2} y={-2} width={annotation.width + 4} height={annotation.height + 4}
						fill="none" stroke="#3182ce" strokeWidth={0.3} strokeDasharray="1,1" />
				)}
				{/* Hit area: a text box with neither fill nor border must still be grabbable */}
				<rect x={0} y={0} width={annotation.width} height={annotation.height}
					fill="transparent" stroke="none" rx={0.5}
					style={{ pointerEvents: 'auto' }} />
				{/* Text box background */}
				<rect x={0} y={0} width={annotation.width} height={annotation.height}
					fill={fill} fillOpacity={fillOpacity}
					stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={strokeDasharray}
					rx={0.5}
					style={{ pointerEvents: 'none' }} />
				<foreignObject x={0} y={0} width={annotation.width} height={annotation.height}>
					{isEditing ? (
						<div style={{ position: 'relative', width: '100%', height: '100%' }}>
							<textarea
								ref={textareaRef}
								value={editText}
								onChange={handleEditChange}
								onKeyDown={handleEditKeyDown}
								onKeyUp={handleTextSelect}
								onMouseUp={handleTextSelect}
								onSelect={handleTextSelect}
								onBlur={handleEditBlur}
								style={{
									...textBoxTextStyle,
									position: 'absolute',
									top: 0,
									left: 0,
									color: 'transparent',
									caretColor: previewTextColor || annotation.textColor || annotation.color,
									background: 'transparent',
									border: 'none',
									outline: 'none',
									resize: 'none',
									cursor: 'text'
								}}
							/>
							{/* Styled mirror over the transparent-text textarea: a textarea itself cannot show per-range formatting */}
							<div
								aria-hidden="true"
								style={{
									...textBoxTextStyle,
									position: 'absolute',
									top: 0,
									left: 0,
									color: previewTextColor || annotation.textColor || annotation.color,
									overflow: 'hidden',
									pointerEvents: 'none'
								}}>
								{renderAnnotationTextRuns(editText, editRuns, textRunOptions)}
							</div>
						</div>
					) : (
						<div
							ref={textMeasureRef}
							style={{
								...textBoxTextStyle,
								color: previewTextColor || annotation.textColor || annotation.color,
								overflow: 'visible',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: justifyForVerticalAlign(annotation.verticalAlign || 'top'),
								pointerEvents: 'none'
							}}>
							<div style={{ width: '100%' }}>
								{annotation.text ? renderAnnotationTextRuns(annotation.text, styleRuns, textRunOptions) : (isSelected ? '' : 'Double-click to edit...')}
							</div>
						</div>
					)}
				</foreignObject>
				{/* PowerPoint-style resize handles: 4 corners + 4 edge midpoints */}
				{isSelected && !isEditing && activeTool === 'select' && renderResizeHandles(annotation.width, annotation.height)}
				{/* Rotation handle: circle above top-center with connecting line */}
				{renderRotationHandle(annotation.width / 2)}
			</g>
		);
	}

	// ===========================
	// Doodle
	// ===========================
	if (annotation.type === SkuAnnotationType.Doodle) {
		const points = annotation.points;
		if (!points || points.length < 2) return null;

		const pathD = points.reduce((d, pt, i) => {
			return i === 0 ? `M ${pt.x} ${pt.y}` : `${d} L ${pt.x} ${pt.y}`;
		}, '');

		const { stroke, strokeWidth, strokeDasharray } = getAnnotationStrokePaint({
			annotation,
			width: annotation.strokeWidth * 0.15,
			dasharray: getStrokeDasharray(annotation.lineStyle),
			previewColor: previewStrokeColor
		});

		return (
			<g key={annotation.id} id={annotation.id} ref={groupRef}
				transform={`translate(${annotation.x}, ${annotation.y})`}
				style={{ cursor: activeTool === 'select' ? 'pointer' : 'default', pointerEvents: 'auto' }}
				onClick={(e) => { e.stopPropagation(); if (activeTool === 'select') onSelectAnnotation(annotation.id); }}
				onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (onContextMenu) onContextMenu(e, annotation.id); }}
			>
				{/* Hit area (wider transparent path for easier clicking), floored so a stroke-less doodle stays clickable */}
				<path d={pathD} fill="none" stroke="transparent" strokeWidth={Math.max(annotation.strokeWidth * 3, MIN_DOODLE_HIT_WIDTH)} style={{ pointerEvents: 'stroke' }} />
				{/* Visible path */}
				<path d={pathD} fill="none" stroke={stroke}
					strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
					strokeDasharray={strokeDasharray} />
				{/* Selection indicator */}
				{isSelected && (
					<path d={pathD} fill="none" stroke="#3182ce"
						strokeWidth={isAnnotationStroked(annotation) ? annotation.strokeWidth * 0.25 : UNSTROKED_SELECTION_WIDTH} strokeLinecap="round"
						strokeLinejoin="round" strokeDasharray="1,1" opacity={0.5} />
				)}
			</g>
		);
	}

	// ===========================
	// Line
	// ===========================
	if (annotation.type === SkuAnnotationType.Line) {
		const { x, y, x2, y2, color, strokeWidth, lineStyle, arrowhead, rotation } = annotation;
		const { stroke: lineStroke, strokeWidth: lineStrokeWidth, strokeDasharray } = getAnnotationStrokePaint({
			annotation,
			width: strokeWidth,
			dasharray: getStrokeDasharray(lineStyle),
			previewColor: previewStrokeColor
		});
		const markerId = `arrowhead-${annotation.id}`;
		const midX = (x + x2) / 2;
		const midY = (y + y2) / 2;

		const hasStartArrow = arrowhead === SkuAnnotationArrowhead.Start || arrowhead === SkuAnnotationArrowhead.Both;
		const hasEndArrow = arrowhead === SkuAnnotationArrowhead.End || arrowhead === SkuAnnotationArrowhead.Both;

		return (
			<g key={annotation.id} id={annotation.id} ref={groupRef}
				transform={getGroupTransform(x, y, rotation) || undefined}
				style={{ cursor: activeTool === 'select' ? 'pointer' : 'default', pointerEvents: 'auto' }}
				onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (onContextMenu) onContextMenu(e, annotation.id); }}
			>
				{/* Arrowhead marker definitions */}
				<defs>
					<marker id={`${markerId}-end`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto" markerUnits="strokeWidth">
						<polygon points="0 0, 10 3.5, 0 7" fill={color} />
					</marker>
					<marker id={`${markerId}-start`} markerWidth="10" markerHeight="7" refX="1" refY="3.5" orient="auto" markerUnits="strokeWidth">
						<polygon points="10 0, 0 3.5, 10 7" fill={color} />
					</marker>
				</defs>
				{/* Hit area (wider transparent line for easier clicking) */}
				<line
					x1={x} y1={y} x2={x2} y2={y2}
					stroke="transparent" strokeWidth={Math.max(strokeWidth * 10, 3)}
					style={{ pointerEvents: 'stroke', cursor: activeTool === 'select' ? 'move' : 'default' }}
					onMouseDown={(e) => { if (activeTool === 'select') handleLineDragStart(e, 'body'); }}
				/>
				{/* Visible line */}
				<line
					x1={x} y1={y} x2={x2} y2={y2}
					stroke={lineStroke} strokeWidth={lineStrokeWidth} strokeLinecap="round"
					strokeDasharray={strokeDasharray}
					markerStart={hasStartArrow ? `url(#${markerId}-start)` : undefined}
					markerEnd={hasEndArrow ? `url(#${markerId}-end)` : undefined}
					style={{ pointerEvents: 'none' }}
				/>
				{/* Selection indicator */}
				{isSelected && (
					<line
						x1={x} y1={y} x2={x2} y2={y2}
						stroke="#3182ce" strokeWidth={isAnnotationStroked(annotation) ? strokeWidth * 1.5 : UNSTROKED_SELECTION_WIDTH} strokeLinecap="round"
						strokeDasharray="1,1" opacity={0.4}
						style={{ pointerEvents: 'none' }}
					/>
				)}
				{/* Endpoint handles when selected */}
				{isSelected && activeTool === 'select' && (
					<>
						<circle
							cx={x} cy={y} r={ENDPOINT_HANDLE_RADIUS}
							fill="white" stroke="#3182ce" strokeWidth={0.15}
							style={{ cursor: 'crosshair', pointerEvents: 'auto' }}
							onMouseDown={(e) => handleLineDragStart(e, 'start')}
						/>
						<circle
							cx={x2} cy={y2} r={ENDPOINT_HANDLE_RADIUS}
							fill="white" stroke="#3182ce" strokeWidth={0.15}
							style={{ cursor: 'crosshair', pointerEvents: 'auto' }}
							onMouseDown={(e) => handleLineDragStart(e, 'end')}
						/>
					</>
				)}
				{/* Rotation handle: circle above the midpoint with connecting line */}
				{renderRotationHandle(midX, midY)}
			</g>
		);
	}

	// ===========================
	// Rectangle
	// ===========================
	if (annotation.type === SkuAnnotationType.Rectangle) {
		const { fill, fillOpacity } = getAnnotationFillPaint({ annotation, previewColor: previewFillColor });
		const { stroke, strokeWidth, strokeDasharray } = getAnnotationStrokePaint({
			annotation,
			width: annotation.strokeWidth,
			dasharray: getStrokeDasharray(annotation.lineStyle),
			previewColor: previewStrokeColor
		});

		return (
			<g key={annotation.id} id={annotation.id} ref={groupRef}
				transform={getGroupTransform(annotation.x, annotation.y, annotation.rotation)}
				style={{ cursor: activeTool === 'select' && !isResizing ? 'move' : 'default', pointerEvents: 'auto' }}
				onClick={(e) => { e.stopPropagation(); if (activeTool === 'select') onSelectAnnotation(annotation.id); }}
				onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (onContextMenu) onContextMenu(e, annotation.id); }}
			>
				{/* Hit area: an unfilled shape is grabbed by its (floored) stroke band, so it stops swallowing interior clicks */}
				<rect x={0} y={0} width={annotation.width} height={annotation.height}
					fill="transparent" stroke="transparent" strokeWidth={Math.max(annotation.strokeWidth * 5, 2)}
					style={{ pointerEvents: isAnnotationFilled(annotation) ? 'auto' : 'stroke' }} />
				{/* Visible rectangle */}
				<rect x={0} y={0} width={annotation.width} height={annotation.height}
					fill={fill} fillOpacity={fillOpacity}
					stroke={stroke} strokeWidth={strokeWidth}
					strokeDasharray={strokeDasharray} rx={0.3}
					style={{ pointerEvents: 'none' }} />
				{/* Selection border */}
				{isSelected && (
					<rect x={-1} y={-1} width={annotation.width + 2} height={annotation.height + 2}
						fill="none" stroke="#3182ce" strokeWidth={0.2} strokeDasharray="1,1"
						style={{ pointerEvents: 'none' }} />
				)}
				{/* Resize handles */}
				{renderResizeHandles(annotation.width, annotation.height)}
				{/* Rotation handle: circle above top-center with connecting line */}
				{renderRotationHandle(annotation.width / 2)}
			</g>
		);
	}

	// ===========================
	// Ellipse
	// ===========================
	if (annotation.type === SkuAnnotationType.Ellipse) {
		const { width, height, strokeWidth: sw, lineStyle } = annotation;
		const cx = width / 2;
		const cy = height / 2;
		const { fill, fillOpacity } = getAnnotationFillPaint({ annotation, previewColor: previewFillColor });
		const { stroke, strokeWidth, strokeDasharray } = getAnnotationStrokePaint({
			annotation,
			width: sw,
			dasharray: getStrokeDasharray(lineStyle),
			previewColor: previewStrokeColor
		});

		return (
			<g key={annotation.id} id={annotation.id} ref={groupRef}
				transform={getGroupTransform(annotation.x, annotation.y, annotation.rotation)}
				style={{ cursor: activeTool === 'select' && !isResizing ? 'move' : 'default', pointerEvents: 'auto' }}
				onClick={(e) => { e.stopPropagation(); if (activeTool === 'select') onSelectAnnotation(annotation.id); }}
				onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (onContextMenu) onContextMenu(e, annotation.id); }}
			>
				{/* Hit area: an unfilled shape is grabbed by its (floored) stroke band, so it stops swallowing interior clicks */}
				<ellipse cx={cx} cy={cy} rx={cx} ry={cy}
					fill="transparent" stroke="transparent" strokeWidth={Math.max(sw * 5, 2)}
					style={{ pointerEvents: isAnnotationFilled(annotation) ? 'auto' : 'stroke' }} />
				{/* Visible ellipse */}
				<ellipse cx={cx} cy={cy} rx={cx} ry={cy}
					fill={fill} fillOpacity={fillOpacity}
					stroke={stroke} strokeWidth={strokeWidth}
					strokeDasharray={strokeDasharray}
					style={{ pointerEvents: 'none' }} />
				{/* Selection border */}
				{isSelected && (
					<rect x={-1} y={-1} width={width + 2} height={height + 2}
						fill="none" stroke="#3182ce" strokeWidth={0.2} strokeDasharray="1,1"
						style={{ pointerEvents: 'none' }} />
				)}
				{/* Resize handles */}
				{renderResizeHandles(width, height)}
				{/* Rotation handle: circle above top-center with connecting line */}
				{renderRotationHandle(cx)}
			</g>
		);
	}

	return null;
};
